import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Play, Clock, Sparkles, RefreshCw, Newspaper } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { PageTransition } from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { getIcon } from "@/lib/icons";

function VideoCard({ v, index, onOpen }) {
  return (
    <motion.button
      data-testid={`whats-new-video-${v.video_id}`}
      onClick={() => onOpen(v.video_id)}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.05, 0.3) }}
      className="group text-left bg-card border border-border shadow-brutal-sm brutal-press flex flex-col overflow-hidden"
    >
      <div className="relative aspect-video bg-secondary border-b border-border overflow-hidden">
        {v.thumbnail ? (
          <img src={v.thumbnail} alt={v.video_title} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="h-full w-full grid place-items-center"><Sparkles className="h-8 w-8 text-muted-foreground" /></div>
        )}
        <span className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity bg-foreground/30">
          <span className="grid place-items-center h-12 w-12 bg-brand-lime border border-border"><Play className="h-5 w-5 fill-current" /></span>
        </span>
        {v.duration_label && (
          <span className="absolute bottom-2 right-2 font-mono text-[10px] font-bold uppercase bg-foreground text-background border border-background px-1.5 py-0.5 inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {v.duration_label}
          </span>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <h4 className="font-heading font-extrabold text-sm leading-snug line-clamp-2">{v.video_title}</h4>
        <p className="mt-1 text-xs text-muted-foreground font-medium truncate">{v.channel}</p>
      </div>
    </motion.button>
  );
}

function Section({ s, onOpen, index }) {
  const Icon = getIcon(s.icon);
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.06, 0.3) }}
      data-testid={s.slug}
    >
      <div className="flex items-start gap-3 mb-4">
        <span className="grid place-items-center h-10 w-10 shrink-0 bg-brand-lime border border-border shadow-brutal-sm"><Icon className="h-5 w-5" /></span>
        <div>
          <h2 className="font-heading font-extrabold tracking-tight text-xl md:text-2xl">{s.title}</h2>
          <p className="text-sm text-muted-foreground font-medium">{s.description}</p>
        </div>
      </div>
      {s.ready && s.videos.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {s.videos.map((v, i) => <VideoCard key={v.video_id} v={v} index={i} onOpen={onOpen} />)}
        </div>
      ) : (
        <div
          className="bg-secondary border border-dashed border-border p-6 flex items-center gap-3"
          data-testid={`${s.slug}-soon`}
        >
          <span className="grid place-items-center h-9 w-9 bg-brand-tangerine text-brand-ink border border-border"><RefreshCw className="h-4 w-4" /></span>
          <p className="text-sm font-medium">
            <span className="font-mono text-[10px] font-bold uppercase bg-brand-tangerine text-brand-ink border border-border px-1.5 py-0.5 mr-1">curating</span>
            Fresh picks are being curated — check back shortly.
          </p>
        </div>
      )}
    </motion.section>
  );
}

export default function WhatsNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(0);
  const language = user?.language || "english";

  const load = async () => {
    try {
      const { data } = await api.get(`/whats-new?language=${encodeURIComponent(language)}`);
      setData(data);
      if (!data.ready && pollRef.current < 5) {
        pollRef.current += 1;
        setTimeout(load, 10000);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openLesson = (videoId) => { if (videoId) navigate(`/lesson/${videoId}`); };

  return (
    <div className="min-h-screen bg-background grid-paper">
      <Navbar />
      <PageTransition className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
        <div className="bg-brand-cyber text-white border border-border shadow-brutal p-6 md:p-8" data-testid="whats-new-header">
          <p className="font-mono text-xs font-bold uppercase tracking-widest opacity-80 inline-flex items-center gap-1.5">
            <Newspaper className="h-4 w-4" /> Always fresh
          </p>
          <h1 className="mt-2 font-heading font-extrabold tracking-tight text-3xl md:text-5xl leading-[0.95]">What's new in AI</h1>
          <p className="mt-3 font-medium max-w-2xl text-white/90">
            The latest AI tools, model releases, news and agent breakthroughs — hand-picked and explained simply. Open to everyone, no matter where you are on your path.
          </p>
          <Button data-testid="whats-new-to-dashboard" onClick={() => navigate("/dashboard")} className="mt-5 bg-primary text-primary-foreground border-border">
            Back to my roadmap
          </Button>
        </div>

        {loading ? (
          <div className="py-24 grid place-items-center" data-testid="whats-new-loading">
            <div className="grid place-items-center h-16 w-16 bg-brand-lime border border-border shadow-brutal">
              <Loader2 className="h-8 w-8 animate-spin text-foreground" />
            </div>
          </div>
        ) : (
          <div className="mt-10 space-y-12" data-testid="whats-new-sections">
            {/* fresh picks — always-populated fallback so the feed is never empty */}
            {(data?.fresh_picks || []).length > 0 && (
              <section data-testid="whats-new-fresh">
                <div className="flex items-start gap-3 mb-4">
                  <span className="grid place-items-center h-10 w-10 shrink-0 bg-brand-pink text-white border border-border shadow-brutal-sm"><Sparkles className="h-5 w-5" /></span>
                  <div>
                    <h2 className="font-heading font-extrabold tracking-tight text-xl md:text-2xl">Fresh from the library</h2>
                    <p className="text-sm text-muted-foreground font-medium">{data.updated_note || "Updated daily."}</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.fresh_picks.map((v, i) => <VideoCard key={v.video_id} v={v} index={i} onOpen={openLesson} />)}
                </div>
              </section>
            )}
            {(data?.sections || []).map((s, i) => (
              <Section key={s.slug} s={s} index={i} onOpen={openLesson} />
            ))}
          </div>
        )}
      </PageTransition>
    </div>
  );
}
